import {EditOutlined} from '@ant-design/icons';
import {Button, Form, Modal, Select, message} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo} from 'react';

import {RqUserLookupAllRole} from '../../../../../ReactQuery/apis/rqAuthAPI';
import {RQUpdatePatientHistory} from '../../../../../ReactQuery/apis/rqPatientHistory';
import PatientHistoryFormFields from '../PatientHistoryFormFields';

// Converts a dayjs value (or anything) to 'YYYY-MM-DD', or null if empty.
const toDateString = value => (value ? dayjs(value).format('YYYY-MM-DD') : null);

// Returns undefined if every value in the object is empty/undefined/null/'',
// otherwise returns the object as-is. Sending `undefined` means the key is
// dropped entirely, which keeps male-patient payloads free of
// menstrualHistory/obstetricHistory objects.
const stripIfEmpty = obj => {
  if (!obj) {return undefined;}

  const hasValue = Object.values(obj).some(
    v => v !== undefined && v !== null && v !== '',
  );

  return hasValue ? obj : undefined;
};

// Builds the exact payload shape the backend expects from raw antd Form values.
const buildPatientHistoryPayload = values => {
  const menstrualHistory = stripIfEmpty({
    ...values.menstrualHistory,
    lmp: toDateString(values.menstrualHistory?.lmp),
  });

  const obstetricHistory = stripIfEmpty({
    ...values.obstetricHistory,
    marriedSince: toDateString(values.obstetricHistory?.marriedSince),
  });

  const examination = stripIfEmpty(values.examination);
  const lifestyle = stripIfEmpty(values.lifestyle);
  const diagnosis = stripIfEmpty(values.diagnosis);

  return {
    patientId: values.patientId,
    symptoms: values.symptoms || [],
    ...(examination && {examination}),
    ...(menstrualHistory && {menstrualHistory}),
    ...(obstetricHistory && {obstetricHistory}),
    ...(lifestyle && {lifestyle}),
    investigation: {
      investigations: values.investigation?.investigations || [],
    },
    ...(diagnosis && {diagnosis}),
  };
};

// Converts a saved history record (from the API) back into antd Form values
// for the Edit form — the inverse of buildPatientHistoryPayload's date handling.
const historyToFormValues = history => ({
  ...history,
  menstrualHistory: {
    ...history.menstrualHistory,
    lmp: history.menstrualHistory?.lmp ? dayjs(history.menstrualHistory.lmp) : null,
  },
  obstetricHistory: {
    ...history.obstetricHistory,
    marriedSince: history.obstetricHistory?.marriedSince
      ? dayjs(history.obstetricHistory.marriedSince)
      : null,
  },
});

const EditPatientHistory = ({open, onClose, history, token, onUpdated}) => {
  const [form] = Form.useForm();
  const updatePatientHistory = RQUpdatePatientHistory();

  const {data: patients = [], isFetching: patientsLoading} = RqUserLookupAllRole('PATIENT', token);

  const patientOptions = useMemo(
    () => patients.map(p => ({
      value: p.id,
      label: p.fullName,
    })),
    [patients],
  );

  useEffect(() => {
    if (open && history) {
      form.setFieldsValue(historyToFormValues(history));
    }
  }, [open, history, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = buildPatientHistoryPayload(values);

      await updatePatientHistory.mutateAsync({
        id: history.patienthistoryId,
        formData,
        token,
      });
      message.success('Patient history updated');
      onUpdated?.();
      handleClose();
    } catch (err) {
      if (err?.errorFields) {return;}

      message.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to update patient history',
      );
    }
  };

  const selectedPatientName = patients.find(p => p.id === history?.patientId)?.fullName;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      width={760}
      title={(
        <span className={'text-primary-700 font-bold text-base flex items-center gap-2'}>
          <EditOutlined />
          Edit Patient History — {selectedPatientName || history?.patientId}
        </span>
      )}
      footer={[
        <Button key={'cancel'} onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key={'submit'}
          type={'primary'}
          className={'btn-primary'}
          loading={updatePatientHistory.isPending}
          onClick={handleSubmit}
        >
          Update History
        </Button>,
      ]}
    >
      <Form form={form} layout={'vertical'} className={'mt-2'}>
        <Form.Item
          name={'patientId'}
          label={'Patient Name'}
          rules={[{required: true, message: 'Please select a patient'}]}
        >
          <Select
            showSearch
            disabled
            placeholder={'Search and select patient…'}
            loading={patientsLoading}
            options={patientOptions}
            optionFilterProp={'label'}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>

        <PatientHistoryFormFields form={form} />
      </Form>
    </Modal>
  );
};

EditPatientHistory.propTypes = {
  history: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
  open: PropTypes.bool.isRequired,
  token: PropTypes.string,
};

export default EditPatientHistory;
